import { Component, OnInit, Input, NgModule, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SendMailFormData } from '@model/customrest/send-mail-form-data';
import { UserService } from '@service/user/user.service';
import { LoadingService } from '@service/loading.service';
import { Entity } from '@model/entities/entity';
import { PrivateMessageService } from '@service/private-message.service';
import { User } from '@model/user/user';
import { ScrollService } from '@service/scroll.service';
import { EntityTypeConfig } from '@model/entity-type-config';
import { EntityTypeConfigService } from '@service/entity-type-config.service';
import { tinyDefaultConfig } from '@configuration/tiny-default-config';
import { SharedModule } from 'src/app/shared.module';
import { EditorModule } from '@tinymce/tinymce-angular';
import { DialogService } from '@service/dialog.service';

@Component({
  selector: 'app-private-message',
  templateUrl: './private-message.component.html',
  styleUrls: ['./private-message.component.scss'],
})
export class PrivateMessageComponent implements OnInit {
  @Input()
  public entity: Entity = null;

  public mailSent = false;

  public formSubmitted = false;

  public form: FormGroup = null;

  // make available for template
  public tinyDefaultConfig = tinyDefaultConfig;

  @ViewChild('privateMessageComponent') set privateMessageComponent(el: ElementRef) {
    this.ScrollService.privateMessageComponent = el;
    this.ScrollService.privateMessageComponentSet.emit();
  }

  constructor(
    public userService: UserService,
    public matDialog: MatDialog,
    private formBuilder: FormBuilder,
    private loadingService: LoadingService,
    private privateMessageService: PrivateMessageService,
    private ScrollService: ScrollService,
    private entityTypeConfigService: EntityTypeConfigService,
    public DialogService: DialogService,
  ) {}

  public ngOnInit() {
    this.initForm();

    // if user is present -> prefill it
    this.userService.userObservable.subscribe((user: User) => {
      this.handleNewUser(user);
    });
  }

  private initForm() {
    this.form = this.formBuilder.group(
      {
        content: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(10000)]],
        useMail: true,
        useTel: false,
        mail: ['', [Validators.required, Validators.email]],
        tel: [''],
      },
      {
        validator: (fg: FormGroup) => {
          if (!fg.value.useMail && !fg.value.useTel) return { atleastone: true };
          return {};
        },
      },
    );

    this.form.get('useMail').valueChanges.subscribe((checked) => {
      if (checked) this.form.get('mail').setValidators([Validators.required, Validators.email]);
      else this.form.get('mail').setValidators(null);
      this.form.get('mail').updateValueAndValidity();
    });

    this.form.get('useTel').valueChanges.subscribe((checked) => {
      if (checked) this.form.get('tel').setValidators([Validators.required, Validators.minLength(3)]);
      else this.form.get('tel').setValidators(null);
      this.form.get('tel').updateValueAndValidity();
    });
  }

  private handleNewUser(user: User) {
    if (!this.form) return;

    let mail = '';
    let tel = '';

    if (user) {
      mail = user.email;
      if (user.private_telephone) tel = user.private_telephone;
    }

    this.form.patchValue({ mail, tel });
  }

  public loginRegisterDialog() {
    this.DialogService.loginRegistrationDialog();
  }

  public submitForm() {
    this.formSubmitted = true;

    if (this.form.valid) {
      this.sendMail().then(() => {
        setTimeout(() => {
          this.ScrollService.scrollToSelector('#privateMessageComponent');
        }, 10);
      });
    }
  }

  private sendMail(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingService.startLoad();

      let data = new SendMailFormData(this.form.value.content, this.entity.id);
      if (this.form.value.useMail) {
        data.useMail = true;
        data.mail = this.form.value.mail;
      }
      if (this.form.value.useTel) {
        data.useTel = true;
        data.tel = this.form.value.tel;
      }

      let entityTypeConfig: EntityTypeConfig = this.entityTypeConfigService.getConfigByName(
        this.entity.entityTypeIdentifier,
      );

      this.privateMessageService
        .sendMail(data, entityTypeConfig.apiModelName)
        .then(() => {
          this.mailSent = true;
          resolve();
        })
        .catch(() => reject())
        .finally(() => {
          this.loadingService.stopLoad();
        });
    });
  }

  public getName() {
    if (this.entity.profile_type == 'BUSINESS') {
      return this.entity.frontend_user.company_profile.getName();
    } else return this.entity.frontend_user.getName();
  }
}

@NgModule({
  declarations: [PrivateMessageComponent],
  imports: [SharedModule, EditorModule],
  exports: [PrivateMessageComponent, SharedModule, EditorModule],
})
export class PrivateMessageComponentModule {}
